import { TemplateResult, html, nothing, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { nanoid } from 'nanoid';
import { BlocksElement } from '../BlocksElement';
import styles from './table.styles';

/**
 * @slot default - The table content
 * @slot caption - The table caption for accessibility which is visually hidden
 * @csspart base - The component's base wrapper.
 * @csspart caption-wrap - The component's caption wrapper.
 * @csspart part-caption - The component's caption slot.
 * @csspart part-default - The component's default slot.
 */

export class BlocksTable extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Aria label attribute
   * - Provides an accessible name for the table
   */
  @property({ type: String })
  arialabel: string | undefined;

  /**
   * Aria described by attribute
   * - Refers to the element that serves as the caption for the table
   */
  @property({ type: String })
  ariaDescribedBy: string | undefined;

  /**
   * Connected callback lifecycle
   * 1) Autogenerates aria-describedby ids to make this accessible if these properties aren't provided
   */
  connectedCallback() {
    super.connectedCallback();
    this.ariaDescribedBy = this.ariaDescribedBy || nanoid(); /* 1 */
  }

  render(): TemplateResult<1> {
    const componentClassName = this.componentClassNames('cat-c-table', {});

    return html`
      <div
        part="base"
        role="table"
        class=${componentClassName}
        aria-label=${ifDefined(this.arialabel)}
        aria-describedby=${ifDefined(this.slotNotEmpty('caption') || this.arialabel ? this.ariaDescribedBy : undefined)}
      >
        ${this.slotNotEmpty('caption') || this.arialabel
          ? html`
              <div part="caption-wrap" id=${ifDefined(this.ariaDescribedBy)} class="cat-c-table__caption cat-u-is-vishidden">
                ${this.slotNotEmpty('caption')
                  ? html` <slot part="part-caption" name="caption"></slot> `
                  : this.arialabel
                    ? this.arialabel
                    : html`${nothing}`}
              </div>
            `
          : html`${nothing}`}
        ${html` <slot part="part-default"></slot> `}
      </div>
    `;
  }
}

if (customElements.get('cat-table') === undefined) {
  customElements.define('cat-table', BlocksTable);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-table': BlocksTable;
  }
}
